import { apiFetch } from "@/lib/api";

export const LASER_MARK_QUERY_KEY = ["manufacturing", "laser-mark"] as const;
export const LASER_MARK_ACTIVE_JOB_QUERY_KEY = [...LASER_MARK_QUERY_KEY, "active-job"] as const;

const LASER_MARK_SESSION_JOB_ID_KEY = "sgs-laser-mark-active-job-id";

export function persistLaserMarkSessionJobId(jobId: number): void {
  try {
    sessionStorage.setItem(LASER_MARK_SESSION_JOB_ID_KEY, String(jobId));
  } catch {
    // ignore private mode / blocked storage
  }
}

export function clearLaserMarkSessionJobId(): void {
  try {
    sessionStorage.removeItem(LASER_MARK_SESSION_JOB_ID_KEY);
  } catch {
    // ignore
  }
}

export type LaserMarkFixtureFamily = "fi3c" | "fi5a";
export type LaserMarkTemplateId = LaserMarkFixtureFamily | "fpoc";

export function laserMarkSkuMarksConfiguration(sku: string): boolean {
  let upper = sku.trim().toUpperCase();
  if (upper.startsWith("ABB-")) upper = upper.slice("ABB-".length);
  return upper.startsWith("FP-OC");
}

export type LaserMarkQueueItem = {
  kind: "batch_line" | "alternate_order";
  batchItemId: number | null;
  alternateOrderId: number | null;
  batchId: number | null;
  sku: string;
  remainingToBuild: number;
  remainingToMark: number;
  reservedUnconfiguredCount: number;
  fixtureFamily: LaserMarkFixtureFamily;
  fixtureCapacity: number;
  defaultMarkQuantity: number;
  salesOrderNumber: string | null;
  mfrShipByDate: string | null;
};

export type LaserMarkQueueResponse = {
  locationId: number;
  generatedAt: string;
  items: LaserMarkQueueItem[];
};

export type LaserMarkSettings = {
  fi3cTemplatePath: string;
  fi5aTemplatePath: string;
  csvPath: string;
  updatedAt: string | null;
};

export type LaserMarkBundledTemplateResponse = {
  family: LaserMarkTemplateId;
  fileName: string;
  sha256: string;
  xml: string;
};

export type LaserMarkJobResponse = {
  jobId: number;
  sku: string;
  configuration: string;
  fixtureFamily: LaserMarkFixtureFamily;
  templateId: LaserMarkTemplateId;
  fixtureCapacity: number;
  quantity: number;
  serials: string[];
  csv: string;
  csvPath?: string;
  templatePath?: string;
  templateSha256?: string;
  burnCompletedAt?: string | null;
  lightBurn: { channel?: string; started?: boolean; detail?: string; ok?: false; error?: string };
};

const LOCAL_LIGHTBURN_BRIDGE_ORIGINS = ["http://127.0.0.1:19842", "http://192.168.0.48:19842"] as const;

export function fetchLaserMarkQueue(): Promise<LaserMarkQueueResponse> {
  return apiFetch<LaserMarkQueueResponse>("/api/manufacturing/laser-mark/queue");
}

export function fetchLaserMarkSettings(): Promise<LaserMarkSettings> {
  return apiFetch<LaserMarkSettings>("/api/manufacturing/laser-mark/settings");
}

export function fetchLaserMarkBundledTemplate(
  family: LaserMarkTemplateId,
): Promise<LaserMarkBundledTemplateResponse> {
  return apiFetch<LaserMarkBundledTemplateResponse>(
    `/api/manufacturing/laser-mark/templates/${encodeURIComponent(family)}`,
  );
}

export function saveLaserMarkSettings(input: {
  fi3cTemplatePath: string;
  fi5aTemplatePath: string;
  csvPath: string;
}): Promise<LaserMarkSettings> {
  return apiFetch<LaserMarkSettings>("/api/manufacturing/laser-mark/settings", {
    method: "PATCH",
    body: input,
  });
}

async function postJobToBridgeOrigin(
  origin: string,
  job: LaserMarkJobResponse,
): Promise<{ detail: string }> {
  const endpoint = `${origin}/print/lightburn`;
  const baseBody = {
    jobId: job.jobId,
    fixtureFamily: job.templateId,
    csv: job.csv,
    csvPath: job.csvPath ?? "",
    templatePath: job.templatePath ?? "",
    templateSha256: job.templateSha256 ?? "",
    start: false,
    forceLoad: true,
  };

  let response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(baseBody),
    signal: AbortSignal.timeout(90_000),
  });

  if (response.status === 428) {
    const bundled = await fetchLaserMarkBundledTemplate(job.templateId);
    response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...baseBody,
        templateSha256: bundled.sha256,
        templateXml: bundled.xml,
      }),
      signal: AbortSignal.timeout(90_000),
    });
  }

  const text = await response.text().catch(() => "");
  let parsed: { ok?: boolean; detail?: string; error?: string } | null = null;
  try {
    parsed = JSON.parse(text) as { ok?: boolean; detail?: string; error?: string };
  } catch {
    parsed = null;
  }

  if (!response.ok || parsed?.ok === false) {
    throw new Error(parsed?.error ?? (text || response.statusText));
  }

  return {
    detail:
      parsed?.detail?.trim() ||
      "Loaded LightBurn template and CSV. Press the foot pedal when ready to mark.",
  };
}

export async function deliverLaserMarkJobToLocalBridge(
  job: LaserMarkJobResponse,
): Promise<LaserMarkJobResponse> {
  for (const origin of LOCAL_LIGHTBURN_BRIDGE_ORIGINS) {
    try {
      const sent = await postJobToBridgeOrigin(origin, job);
      void apiFetch(`/api/manufacturing/laser-mark/jobs/${String(job.jobId)}/mark-lightburn-loaded`, {
        method: "POST",
      }).catch(() => {});
      return {
        ...job,
        lightBurn: {
          channel: "bridge",
          started: false,
          detail: sent.detail,
        },
      };
    } catch {
      // Try next bridge candidate
    }
  }

  return job;
}

export async function deliverLaserMarkJobToLocalBridgeIfNeeded(
  job: LaserMarkJobResponse,
): Promise<LaserMarkJobResponse> {
  if (job.lightBurn.ok === false) {
    return await deliverLaserMarkJobToLocalBridge(job);
  }
  return job;
}

export function fetchActiveLaserMarkJob(): Promise<LaserMarkJobResponse | null> {
  return apiFetch<LaserMarkJobResponse | null>("/api/manufacturing/laser-mark/jobs/active");
}

export async function createLaserMarkJob(input: {
  kind: "batch_line" | "alternate_order";
  batchItemId?: number | null;
  alternateOrderId?: number | null;
  quantity: number;
}): Promise<LaserMarkJobResponse> {
  const job = await apiFetch<LaserMarkJobResponse>("/api/manufacturing/laser-mark/jobs", {
    method: "POST",
    body: input,
  });
  return await deliverLaserMarkJobToLocalBridge(job);
}

export type LaserMarkBurnStatus = {
  ok: boolean;
  phase: "idle" | "armed" | "burning" | "completed" | string;
  jobId: number;
  completedJobId: number;
};

export async function fetchLaserMarkBurnStatus(): Promise<LaserMarkBurnStatus | null> {
  for (const origin of LOCAL_LIGHTBURN_BRIDGE_ORIGINS) {
    try {
      const response = await fetch(`${origin}/burn-status`, { signal: AbortSignal.timeout(800) });
      if (!response.ok) continue;
      return (await response.json()) as LaserMarkBurnStatus;
    } catch {
      // Try the next bridge.
    }
  }
  return null;
}

export function markLaserMarkBurnCompleted(jobId: number): Promise<{ ok: true; jobId: number }> {
  return apiFetch(`/api/manufacturing/laser-mark/jobs/${String(jobId)}/mark-burn-completed`, {
    method: "POST",
  });
}

export function voidLaserMarkJob(jobId: number): Promise<{ ok: true; jobId: number; serials: string[] }> {
  return apiFetch(`/api/manufacturing/laser-mark/jobs/${String(jobId)}/void`, {
    method: "POST",
  });
}

export function reportLaserMarkFailedBurn(input: {
  serial: string;
}): Promise<{ serial: string; jobId: number; sku: string; qualityIncidentId: number }> {
  return apiFetch("/api/manufacturing/laser-mark/failed-burn", {
    method: "POST",
    body: input,
  });
}

export async function resendLaserMarkJob(input: {
  jobId: number;
}): Promise<LaserMarkJobResponse> {
  const job = await apiFetch<LaserMarkJobResponse>(
    `/api/manufacturing/laser-mark/jobs/${String(input.jobId)}/send-lightburn`,
    {
      method: "POST",
    },
  );
  return await deliverLaserMarkJobToLocalBridge(job);
}

export type LaserMarkPreviewResponse = {
  sku: string;
  configuration: string;
  fixtureFamily: LaserMarkFixtureFamily;
  fixtureCapacity: number;
  quantity: number;
  slotIndex: number;
  serial: string;
  serials: string[];
  datamatrixModules: boolean[][];
  reserved: false;
};

export function fetchLaserMarkPreview(input: {
  kind: "batch_line" | "alternate_order";
  batchItemId?: number | null;
  alternateOrderId?: number | null;
  quantity: number;
  slotIndex: number;
}): Promise<LaserMarkPreviewResponse> {
  const params = new URLSearchParams();
  params.set("kind", input.kind);
  params.set("quantity", String(input.quantity));
  params.set("slotIndex", String(input.slotIndex));
  if (input.batchItemId != null) params.set("batchItemId", String(input.batchItemId));
  if (input.alternateOrderId != null) params.set("alternateOrderId", String(input.alternateOrderId));
  return apiFetch<LaserMarkPreviewResponse>(`/api/manufacturing/laser-mark/preview?${params.toString()}`);
}

export function laserMarkLineKey(item: LaserMarkQueueItem): string {
  if (item.kind === "alternate_order") {
    return `alternate:${String(item.alternateOrderId ?? "")}`;
  }
  return `batch:${String(item.batchItemId ?? "")}`;
}
